import './feedback-scene.css';
const reasons: Record<string, string> = {
  ok: '已采集',
  not_registered: '游戏尚未接入',
  not_captured: '未采集',
  unavailable: '采集通道不可用',
  timeout: '游戏未在时限内响应',
  provider_error: '游戏状态读取失败',
  capture_failed: '截图失败（画布不可读取或渲染器未支持）',
  too_large: '数据超过大小限制',
  invalid: '返回内容无效',
  excluded: '用户未附上截图',
};
export function SceneSummary({
  scene,
  id,
  admin = false,
  automatic = false,
}: {
  scene: any;
  id: string;
  admin?: boolean;
  automatic?: boolean;
}) {
  if (!scene) return <p className="scene-missing">这条历史反馈没有保存现场快照。</p>;
  const image =
    '/api/' + (admin ? 'admin/' : '') + 'support/' + encodeURIComponent(id) + '/scene-image';
  return (
    <section className="feedback-scene">
      <h3>问题现场</h3>
      <p className="scene-caption">
        {automatic ? '自动采集的诊断快照，最多保留14天，并受全局容量限制。采集于' : '随反馈保存的快照，不随诊断日志清理。采集于'}{' '}
        {new Date(scene.capturedAt || scene.receivedAt).toLocaleString()}
      </p>
      <div className="scene-facts">
        <span>
          游戏版本 <b>{scene.environment?.gameVersion || '未知'}</b>
        </span>
        <span>
          最近阶段 <b>{scene.stage || '未提供'}</b>
        </span>
        <span>
          现场采集 <b>{reasons[scene.captureStatus] || scene.captureStatus}</b>
        </span>
      </div>
      <details>
        <summary>存档快照 · {scene.save?.status === 'ok' ? '已保存独立副本' : '未获取'}</summary>
        <p>采集时复制的诊断副本，不会覆盖原存档，也不代表已同步到云端。</p>
        {scene.save?.status === 'ok' ? <>
          {scene.save.redacted && <p>已过滤敏感字段，此副本不保证可直接恢复游戏。</p>}
          <pre>{JSON.stringify(scene.save.value,null,2)}</pre>
        </> : <p>{({not_configured:'游戏未配置统一存档',unavailable:'游戏尚未支持或采集通道不可用',read_failed:'存档读取失败',too_large:'存档超过快照限制',invalid:'存档快照格式无效',timeout:'读取超时'} as Record<string,string>)[scene.save?.status] || '这条历史反馈未保存存档快照'}</p>}
      </details>
      <div className="scene-visuals">
        <div>
          <h4>{scene.screenshotSource === 'user-upload' ? '用户上传截图' : '辅助截图'}</h4>
          {scene.hasImage ? (
            <a href={image} target="_blank" rel="noreferrer">
              <img className="scene-image" src={image} alt="提交反馈时的游戏画面" />
            </a>
          ) : (
            <p className="scene-missing">
              {reasons[scene.screenshotStatus] || scene.screenshotStatus}
              {automatic ? '。自动现场不采集截图。' : '。可请用户通过沟通记录补充截图。'}
            </p>
          )}
        </div>
        <div>
          <h4>游戏状态</h4>
          {scene.stateStatus === 'ok' ? (
            <pre>{JSON.stringify(scene.state, null, 2)}</pre>
          ) : (
            <p className="scene-missing">{reasons[scene.stateStatus] || scene.stateStatus}</p>
          )}
        </div>
      </div>
      {scene.telemetry && <p className="scene-caption">记录保留策略：有限容量，优先保留错误和启动阶段。累计轮换/丢弃 {scene.telemetry.dropped} 条 · 上传失败重试 {scene.telemetry.retries} 次 · 待上传 {scene.telemetry.pending} 条 · 成功请求汇总 {scene.telemetry.successfulRequests} 次。</p>}
      <details open>
        <summary>
          最近运行记录 · {(scene.records?.length || 0) + (scene.serverRecords?.length || 0)} 条
        </summary>
        {[
          ['游戏现场缓存', scene.records],
          ['服务器已收到的记录', scene.serverRecords],
        ].map(([label, entries]: any) => (
          <div key={label}>
            <h4>{label}</h4>
            {entries?.length ? (
              <ol className="scene-records">
                {entries.map((e: any, i: number) => (
                  <li key={i}>
                    <small>
                      +{Math.round((e.elapsed || 0) / 1000)}秒 · {({action:'用户操作',state:'状态变化',ready:'游戏就绪',error:'运行错误',resource:'请求/资源失败',stage:'加载阶段',breadcrumb:'运行记录'} as Record<string,string>)[e.kind] || e.kind}{' '}
                      {e.stage && '· ' + e.stage}
                    </small>
                    <p>{e.message || '—'}</p>
                    {e.file && <code>{e.file}</code>}
                    {e.stack && (
                      <details>
                        <summary>错误堆栈</summary>
                        <pre>{e.stack}</pre>
                      </details>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="scene-missing">
                没有采集到记录。加载早期失败、未接入或采集通道异常都可能导致缺失。
              </p>
            )}
          </div>
        ))}
      </details>
      <details>
        <summary>设备与环境</summary>
        <pre>{JSON.stringify(scene.environment, null, 2)}</pre>
      </details>
    </section>
  );
}
